import React from 'react';
import { PiCaretRight } from 'react-icons/pi';

import { Checkbox } from 'antd';
import Chip from 'components/common/Chip/Chip';
import { STEP_SELECTOR_SEED_TREATMENT_CHANGE_DETAILS_LABEL } from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInputTask } from 'interfaces/IInputTask';

import OptionRow from './OptionRow';
import SeedTreatmentFlyout from './SeedTreatmentFlyout';
import { getSeedTreatmentOptionDisplayLabel } from './stepCalculations';
import {
  getStepOptionId,
  PICKER_OPTION_ID_ATTRIBUTE,
  SeedDressDraft,
  stepOptionsMatch,
  StepSelectorOption,
} from './stepConfig';

interface SeedTreatmentProductPickerProps {
  products: StepSelectorOption[];
  task: IInputTask;
  gUnits: GUnit[];
  anchorRef: React.RefObject<HTMLElement>;
  isMultiSelectMode: boolean;
  checkedItems: StepSelectorOption[];
  checkedSeeds: StepSelectorOption[];
  highlightedProduct: StepSelectorOption | null;
  isFlyoutOpen: boolean;
  isFlyoutOpenedByKeyboard: boolean;
  draft: SeedDressDraft;
  onDraftChange: (changes: Partial<SeedDressDraft>) => void;
  onProductClick: (
    product: StepSelectorOption,
    event: React.MouseEvent
  ) => void;
  onChangeDetails: (product: StepSelectorOption) => void;
  onAdd: (event: React.MouseEvent) => void;
  onCloseFlyout: () => void;
}

/**
 * Seed Treatment product list for the add-new-step (enableMultiSelect) flow.
 * - Clicking a product opens SeedTreatmentFlyout (rate mode, Seed treated, rate/unit, cost).
 * - Single-select: Add commits the step.
 * - Multi-select: clicking an unchecked row checks it (labelled "Product (Seed, Variety)") and
 *   opens the flyout, where Add updates the staged values; clicking a checked row unchecks it;
 *   the Change Details chip reopens the flyout with the staged values.
 * - Fully controlled by StepInputSelector so useStepSelectorKeyboard can drive it.
 * Editing an existing SeedDress step uses SeedTreatmentTwoPanel instead.
 */
const SeedTreatmentProductPicker: React.FC<SeedTreatmentProductPickerProps> = ({
  products,
  task,
  gUnits,
  anchorRef,
  isMultiSelectMode,
  checkedItems,
  checkedSeeds,
  highlightedProduct,
  isFlyoutOpen,
  isFlyoutOpenedByKeyboard,
  draft,
  onDraftChange,
  onProductClick,
  onChangeDetails,
  onAdd,
  onCloseFlyout,
}) => {
  const isHighlightedProductStaged =
    !!highlightedProduct &&
    checkedItems.some((checked) =>
      stepOptionsMatch(checked, highlightedProduct)
    );

  return (
    <>
      {products.map((product) => {
        const checkedEntry = checkedItems.find((checked) =>
          stepOptionsMatch(checked, product)
        );
        const isHighlighted =
          !!highlightedProduct && stepOptionsMatch(highlightedProduct, product);
        return (
          <OptionRow
            key={`${product.item}-${product.globalId ?? 'none'}`}
            navAttributes={{
              [PICKER_OPTION_ID_ATTRIBUTE]: getStepOptionId(product),
            }}
            label={
              checkedEntry
                ? getSeedTreatmentOptionDisplayLabel(
                    checkedEntry,
                    task,
                    checkedSeeds
                  )
                : product.label
            }
            metaOption={product}
            isHighlighted={isHighlighted}
            isFilled={!!checkedEntry || isHighlighted}
            onClick={(event) => onProductClick(product, event)}
            leading={
              isMultiSelectMode && (
                <Checkbox
                  checked={!!checkedEntry}
                  className="mt-1"
                  onClick={(event) => {
                    event.stopPropagation();
                    onProductClick(product, event);
                  }}
                />
              )
            }
            trailing={
              isMultiSelectMode && checkedEntry ? (
                <span className="flex-shrink-0 self-center">
                  <Chip
                    id={`change-details-${getStepOptionId(product)}`}
                    label={STEP_SELECTOR_SEED_TREATMENT_CHANGE_DETAILS_LABEL}
                    onClick={() => onChangeDetails(product)}
                  />
                </span>
              ) : (
                <PiCaretRight
                  size={14}
                  className="flex-shrink-0 self-center text-gray-medium"
                  aria-hidden="true"
                />
              )
            }
          />
        );
      })}

      {isFlyoutOpen && highlightedProduct && (
        <SeedTreatmentFlyout
          key={highlightedProduct.globalId ?? highlightedProduct.item}
          anchorRef={anchorRef}
          title={highlightedProduct.label}
          isMultiSelectMode={isMultiSelectMode}
          autoFocusRateMode={isFlyoutOpenedByKeyboard}
          task={task}
          gUnits={gUnits}
          rateMode={draft.rateMode}
          rate={draft.rate}
          unit={draft.unit}
          cost={draft.cost}
          linkedStepId={draft.linkedStepId}
          checkedSeeds={checkedSeeds}
          autoSelectOnMount={!isHighlightedProductStaged}
          onRateModeChange={(rateMode) => onDraftChange({ rateMode })}
          onRateChange={(rate) => onDraftChange({ rate })}
          onUnitChange={(unit) => onDraftChange({ unit })}
          onCostChange={(cost) => onDraftChange({ cost })}
          onTargetSelect={(linkedStepId) => onDraftChange({ linkedStepId })}
          onDone={onAdd}
          onClose={onCloseFlyout}
        />
      )}
    </>
  );
};

export default SeedTreatmentProductPicker;
